/**
 * Booking-intake write path — the electronic Auftrag-Eingangs-Buch.
 *
 * § 49 Abs. 4 Satz 4 PBefG requires the arrival of every Beförderungsauftrag to
 * be recorded, in a book or electronically, and kept for a year. The paper
 * product the trade uses for this is the HUSS 19006 Auftrag-Eingangs-Buch; its
 * columns are transcribed in `01_Input/referenz/README.md` and this module
 * implements them.
 *
 * Two properties of the paper book have to survive the move to software:
 *
 * 1. The `Lfd. Nr.` is unbroken. A bound book proves completeness by its
 *    numbering, and the cover states which numbers it covers. So numbers are
 *    drawn at the moment of recording, never reused, and gaps are reported
 *    rather than quietly tolerated.
 * 2. The place of acceptance is a closed choice of three. § 49 Abs. 4 Satz 2
 *    only permits orders taken at the Betriebssitz or in the owner's home;
 *    Satz 3 adds orders received by radio or phone during a trip. Free text
 *    here would let an unlawful pickup look like a clerical variation.
 */

import type { AuditContext } from '@/db/audit';
import { highestOrderSequence, orderRepo, orderSequencesInYear } from '@/db/repositories';
import { nowIso } from '@/domain/dates';
import type {
  IsoInstant,
  Order,
  OrderAcceptancePlace,
  OrderChannel,
  Uuid,
} from '@/domain/types';
import { de } from '@/i18n/de';
import type { Dictionary } from '@/i18n/de';

/**
 * The German wording, for the exports.
 *
 * Screens read the same labels from the active dictionary; these two constants
 * are pinned to German because they end up in the PDF, the workbook and the
 * GoBD archive, which are addressed to the Finanzamt whatever the interface
 * language happens to be.
 */
export const CHANNEL_LABELS: Record<OrderChannel, string> = de.labels.channel;

/** The tick box each channel occupies on the paper form, where it has one. */
export const CHANNEL_SYMBOLS: Partial<Record<OrderChannel, string>> = {
  telefon: '☎',
  email: '✉',
  funk: '⚡',
  persoenlich: 'mündlich',
};

export const ACCEPTANCE_LABELS: Record<OrderAcceptancePlace, string> = de.labels.acceptance;

/** The `B | W | ⚡` shorthand printed in the order book. */
export const ACCEPTANCE_SHORT: Record<OrderAcceptancePlace, string> = {
  betriebssitz: 'B',
  wohnung: 'W',
  funk: '⚡',
};

export interface OrderInput {
  receivedAt: IsoInstant;
  channel: OrderChannel;
  ordererName?: string;
  ordererAddress?: string;
  ordererPhone?: string;
  ordererEmail?: string;
  pickupLocation: string;
  destination?: string;
  requestedPickupAt?: IsoInstant;
  acceptedAt?: IsoInstant;
  acceptancePlace?: OrderAcceptancePlace;
  vehicleId?: Uuid;
  driverId?: Uuid;
  passengerId?: Uuid;
  notes?: string;
}

/**
 * The next `Lfd. Nr.` for the year the order arrived in.
 *
 * Derived from the stored rows rather than a saved counter, for the same
 * reason invoice numbers are: a restored backup must not be able to
 * desynchronise the sequence from the data it describes.
 */
export async function nextOrderSequence(receivedAt: IsoInstant): Promise<number> {
  return (await highestOrderSequence(receivedAt.slice(0, 4))) + 1;
}

export async function createOrder(ctx: AuditContext, input: OrderInput): Promise<Order> {
  const order: Order = {
    id: crypto.randomUUID(),
    sequenceNumber: await nextOrderSequence(input.receivedAt),
    ...input,
    createdAt: nowIso(),
  };
  return orderRepo.create(ctx, order);
}

/**
 * Record that the job was taken on, and where.
 *
 * Separate from creation because the two happen at different moments: the call
 * is logged when it comes in, the acceptance when a car is assigned to it.
 */
export async function acceptOrder(
  ctx: AuditContext,
  orderId: Uuid,
  acceptance: {
    acceptancePlace: OrderAcceptancePlace;
    acceptedAt?: IsoInstant;
    vehicleId?: Uuid;
    driverId?: Uuid;
  },
): Promise<Order> {
  return orderRepo.update(ctx, orderId, {
    acceptancePlace: acceptance.acceptancePlace,
    acceptedAt: acceptance.acceptedAt ?? nowIso(),
    vehicleId: acceptance.vehicleId,
    driverId: acceptance.driverId,
  });
}

export interface OrderFinding {
  field: keyof Order | 'sequence';
  /** Rendered late, so the wording follows the interface language. */
  message: (t?: Dictionary) => string;
  /** `pflicht` blocks nothing but is printed as a defect; `hinweis` informs. */
  severity: 'pflicht' | 'hinweis';
}

/**
 * Check one order against the columns the paper form marks as mandatory.
 *
 * Findings are reported, not enforced. A dispatcher taking a call cannot always
 * know the destination, and a half-recorded order beats an unrecorded one —
 * but the gap has to be visible on screen and in the export rather than
 * discovered by an auditor.
 *
 * Ordered by legal weight, not by the order of the columns: the list screen
 * shows the first finding, and where the job was accepted decides whether the
 * trip was lawful, while a missing customer name does not.
 */
export function reviewOrder(order: Order): OrderFinding[] {
  const findings: OrderFinding[] = [];

  if (!order.pickupLocation.trim()) {
    findings.push({
      field: 'pickupLocation',
      message: (t = de) => t.orders.findings.pickupLocation,
      severity: 'pflicht',
    });
  }

  if (!order.acceptancePlace) {
    findings.push({
      field: 'acceptancePlace',
      message: (t = de) => t.orders.findings.acceptancePlace,
      severity: order.tripId ? 'pflicht' : 'hinweis',
    });
  } else if (!order.acceptedAt) {
    findings.push({
      field: 'acceptedAt',
      message: (t = de) => t.orders.findings.acceptedAt,
      severity: 'pflicht',
    });
  }

  if (order.acceptancePlace && !order.vehicleId) {
    findings.push({
      field: 'vehicleId',
      message: (t = de) => t.orders.findings.vehicleId,
      severity: 'pflicht',
    });
  }

  if (!order.ordererName?.trim()) {
    findings.push({
      field: 'ordererName',
      message: (t = de) => t.orders.findings.ordererName,
      severity: 'hinweis',
    });
  }

  return findings;
}

/**
 * Numbers missing from a year's sequence.
 *
 * A bound book cannot lose a page without it showing. This is the electronic
 * equivalent, and it is surfaced in the export so the operator finds the hole
 * before the Betriebsprüfer does.
 */
export async function findOrderSequenceGaps(year: string): Promise<number[]> {
  const issued = await orderSequencesInYear(year);
  if (issued.length === 0) return [];

  const present = new Set(issued);
  const missing: number[] = [];
  // The sequence starts at 1; a year whose first order is numbered 3 has lost two.
  for (let expected = 1; expected <= issued[issued.length - 1]!; expected += 1) {
    if (!present.has(expected)) missing.push(expected);
  }
  return missing;
}
